import { audioContext } from './sfx'

/**
 * ดนตรีคลอ สังเคราะห์สดทั้งหมด ไม่มีไฟล์เพลง
 *
 * ใช้วิธี lookahead scheduler: ตั้ง setInterval ถี่ ๆ คอยจองโน้ตล่วงหน้าราว 0.2 วินาที
 * ลงบนนาฬิกาของ AudioContext (ไม่ใช้ setTimeout ยิงโน้ตตรง ๆ เพราะจังหวะจะเพี้ยน
 * เวลาเบราว์เซอร์ไปทำงานอื่น)
 *
 * มีสี่ชั้น: แพดคอร์ด · เบส · อาร์เพจจิโอ · เพอร์คัสชันเบา ๆ
 * ส่งผ่าน delay ป้อนกลับเป็นรีเวิร์บจำลอง ให้ฟังกว้างแบบอวกาศ
 */

const STORAGE_KEY = 'millo.music.v1'
const BPM = 74
const STEPS_PER_BAR = 16
const LOOKAHEAD_MS = 25
const SCHEDULE_AHEAD = 0.22

export type Mood = 'calm' | 'tense' | 'victory'

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12)

/** คอร์ดในคีย์ C — ใช้คอร์ดทบเจ็ด/เก้าให้ฟังอบอุ่นแบบ lo-fi */
const CALM: number[][] = [
  [53, 60, 64, 67, 72], // Fmaj9
  [57, 64, 67, 71, 74], // Am9
  [50, 57, 60, 65, 69], // Dm9
  [46, 58, 62, 65, 69], // Bbmaj7
]

/** โหมดตึงเครียด: ไมเนอร์ลง แทรกโน้ตครึ่งเสียงให้รู้สึกไม่มั่นคง */
const TENSE: number[][] = [
  [45, 57, 60, 64, 67], // Am
  [44, 56, 59, 63, 68], // Ab-ish
  [43, 55, 58, 62, 65], // Gm
  [42, 54, 59, 61, 66],
]

const VICTORY: number[][] = [
  [48, 60, 64, 67, 72], // C
  [53, 60, 65, 69, 72], // F
  [55, 62, 67, 71, 74], // G
  [48, 60, 64, 67, 76],
]

const CHORDS: Record<Mood, number[][]> = { calm: CALM, tense: TENSE, victory: VICTORY }

/** จังหวะกลอง: 1 = มี, 0 = ไม่มี (16 ช่องต่อห้อง) */
const KICK = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0]
const HAT = [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 1, 0]
const ARP = [1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1]

let enabled = read()
let running = false
let timer: ReturnType<typeof setInterval> | null = null
let step = 0
let nextTime = 0
let bus: GainNode | null = null
let wet: GainNode | null = null
let mood: Mood = 'calm'
let arpIndex = 0

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    return true
  }
}

export const musicEnabled = (): boolean => enabled

/** โหนดรวมของดนตรี — เปิดไว้ให้วัดระดับเสียงและใช้หรี่เพลงตอนมีเหตุการณ์ใหญ่ */
export const musicBus = (): GainNode | null => bus

/** สถานะภายใน ใช้ตอนไล่ปัญหาว่าทำไมเพลงไม่ดัง */
export const musicDebug = () => ({ enabled, running, hasBus: !!bus, hasTimer: !!timer, step, mood })

export function setMusicEnabled(on: boolean): void {
  enabled = on
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off')
  } catch {
    /* ไม่เป็นไร */
  }
  if (on) startMusic()
  else stopMusic()
}

export function setMood(next: Mood): void {
  mood = next
}

function build(ctx: AudioContext): void {
  if (bus) return
  bus = ctx.createGain()
  bus.gain.value = 0
  bus.connect(ctx.destination)

  // รีเวิร์บจำลองด้วย delay ป้อนกลับ + กรองความถี่สูงออก ฟังนุ่มกว่า delay เปล่า ๆ
  const delay = ctx.createDelay(1.2)
  delay.delayTime.value = 0.36
  const fb = ctx.createGain()
  fb.gain.value = 0.34
  const damp = ctx.createBiquadFilter()
  damp.type = 'lowpass'
  damp.frequency.value = 2200
  wet = ctx.createGain()
  wet.gain.value = 0.5

  delay.connect(damp).connect(fb).connect(delay)
  damp.connect(wet).connect(bus)
}

export function startMusic(): void {
  const ctx = audioContext()
  if (!ctx || !enabled || running) return
  build(ctx)
  running = true
  step = 0
  nextTime = ctx.currentTime + 0.1
  bus!.gain.cancelScheduledValues(ctx.currentTime)
  bus!.gain.setValueAtTime(bus!.gain.value, ctx.currentTime)
  // ระดับนี้วัดแล้วได้ราว −26 dBFS ซึ่งเป็นระดับเพลงคลอที่ไม่กลบเสียงเอฟเฟกต์
  bus!.gain.linearRampToValueAtTime(0.46, ctx.currentTime + 2.5)
  timer = setInterval(tick, LOOKAHEAD_MS)
}

export function stopMusic(): void {
  const ctx = audioContext()
  running = false
  if (timer) clearInterval(timer)
  timer = null
  if (bus && ctx) {
    bus.gain.cancelScheduledValues(ctx.currentTime)
    bus.gain.setValueAtTime(bus.gain.value, ctx.currentTime)
    bus.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6)
  }
}

function tick(): void {
  const ctx = audioContext()
  if (!ctx || !running) return
  const stepDur = 60 / BPM / 4
  while (nextTime < ctx.currentTime + SCHEDULE_AHEAD) {
    schedule(ctx, step, nextTime)
    nextTime += stepDur
    step = (step + 1) % (STEPS_PER_BAR * 4)
  }
}

/* ------------------------------------------------------------------ */
/* เครื่องดนตรี                                                       */
/* ------------------------------------------------------------------ */

function voice(
  ctx: AudioContext,
  freq: number,
  t: number,
  dur: number,
  type: OscillatorType,
  peak: number,
  attack = 0.01,
  send = 0.5,
  /** true = ค้างระดับไว้แล้วค่อยคลายตอนท้าย ใช้กับแพดคอร์ดไม่ให้เงียบกลางห้อง */
  sustain = false,
): void {
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = type
  osc.frequency.setValueAtTime(freq, t)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(peak, t + attack)
  if (sustain) g.gain.setValueAtTime(peak, t + dur * 0.68)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
  osc.connect(g)
  g.connect(bus!)
  if (wet) {
    const s = ctx.createGain()
    s.gain.value = send
    g.connect(s)
    s.connect(wet)
  }
  osc.start(t)
  osc.stop(t + dur + 0.05)
}

function noiseHit(ctx: AudioContext, t: number, dur: number, freq: number, peak: number): void {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * dur))
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1
  const src = ctx.createBufferSource()
  src.buffer = buf
  const bp = ctx.createBiquadFilter()
  bp.type = 'highpass'
  bp.frequency.value = freq
  const g = ctx.createGain()
  g.gain.setValueAtTime(peak, t)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
  src.connect(bp).connect(g).connect(bus!)
  src.start(t)
  src.stop(t + dur)
}

function kick(ctx: AudioContext, t: number): void {
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(120, t)
  osc.frequency.exponentialRampToValueAtTime(42, t + 0.13)
  g.gain.setValueAtTime(0.28, t)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2)
  osc.connect(g).connect(bus!)
  osc.start(t)
  osc.stop(t + 0.24)
}

function schedule(ctx: AudioContext, s: number, t: number): void {
  const bar = Math.floor(s / STEPS_PER_BAR)
  const inBar = s % STEPS_PER_BAR
  const chords = CHORDS[mood]
  const chord = chords[bar % chords.length]

  // แพดคอร์ด — เข้าช้าออกช้า เป็นพื้นหลังของทุกอย่าง
  if (inBar === 0) {
    const dur = (60 / BPM) * 4 * 0.99
    chord.slice(1).forEach((n, i) => {
      voice(ctx, midi(n), t, dur, 'triangle', 0.062 - i * 0.005, 0.9, 0.7, true)
      // เพี้ยนความถี่นิดหน่อยให้เสียงหนา
      voice(ctx, midi(n) * 1.004, t, dur, 'sine', 0.042, 1.1, 0.7, true)
    })
  }

  // เบส
  if (inBar === 0 || inBar === 10) {
    voice(ctx, midi(chord[0] - 12), t, 0.95, 'sine', 0.26, 0.02, 0.15, true)
  }

  // อาร์เพจจิโอ — ไล่โน้ตในคอร์ดแบบเดินสุ่มเบา ๆ ไม่ให้ฟังซ้ำจนน่าเบื่อ
  if (ARP[inBar]) {
    if (Math.random() < 0.34) arpIndex += Math.random() < 0.5 ? 1 : -1
    else arpIndex += 1
    const tones = chord.slice(1)
    const idx = ((arpIndex % tones.length) + tones.length) % tones.length
    const octave = Math.random() < 0.22 ? 12 : 0
    voice(ctx, midi(tones[idx] + octave), t, 0.62, 'triangle', 0.105, 0.006, 0.8)
  }

  if (KICK[inBar]) kick(ctx, t)
  if (HAT[inBar]) noiseHit(ctx, t, 0.045, 7200, 0.035)

  // เสียงระยิบตอนขึ้นห้องใหม่ ให้รู้สึกเป็นอวกาศ
  if (inBar === 0 && bar % 2 === 1) {
    voice(ctx, midi(chord[chord.length - 1] + 12), t, 1.6, 'sine', 0.03, 0.5, 1)
  }
}

/** หยุดเล่นตอนสลับแท็บ ไม่ให้กินซีพียูและเสียงไปดังตอนผู้ใช้ไม่ได้ดู */
export function bindVisibility(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopMusic()
    else if (enabled) startMusic()
  })
}
